import { useCallback, useEffect, useRef, useState } from 'react';
import { api } from './api.js';
import type { GenerationJob, JobStatus } from '@muidsoft/shared';

/**
 * Interval polling (ms) per JobStatus.
 *
 * Prinsip: semakin "aktif" stage (LLM sedang jalan), poll lebih rapat.
 *          semakin "pause" stage (nunggu user), poll lebih jarang.
 *          stage terminal → 0 = stop polling.
 *
 * Mapping logis:
 * - queued         : Worker belum terima pesan, kemungkinan antre di Queue
 * - planning       : Worker sudah terima, sedang setup (sangat cepat)
 * - concepts_ready : pause → nunggu user pilih konsep, hemat request
 * - storyboard_ready: transisi cepat → segera advance ke awaiting_approval
 * - awaiting_approval: pause → nunggu user approve storyboard
 * - exporting_prompts: LLM aktif → poll rapat supaya progress terasa live
 * - completed/failed : terminal → stop
 */
const INTERVAL_BY_STATUS: Record<JobStatus, number> = {
  queued:              6000,
  planning:            3000,
  concepts_ready:      12000,  // pause — user pilih konsep
  storyboard_ready:    4000,   // transisi cepat ke awaiting_approval
  awaiting_approval:   12000,  // pause — user review storyboard
  exporting_prompts:   2500,   // LLM aktif — poll rapat
  completed:           0,      // terminal — stop
  failed:              0,      // terminal — stop
};

const RETRY_INTERVAL_ON_ERROR = 8000; // retry lebih lambat kalau network error

/**
 * useJobPolling — poll GET /jobs/:id dengan interval adaptif.
 *
 * Berhenti otomatis saat status `completed` atau `failed`.
 * Retry otomatis (lebih lambat) kalau request gagal karena network error.
 * Expose `refresh()` untuk trigger poll segera dari luar (misal setelah aksi user).
 */
export function useJobPolling(jobId: string | null) {
  const [job, setJob] = useState<GenerationJob | null>(null);
  const [error, setError] = useState<string | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Ref ke fungsi poll aktif — digunakan oleh refresh() tanpa perlu re-render
  const pollRef = useRef<(() => Promise<void>) | null>(null);

  useEffect(() => {
    if (!jobId) {
      setJob(null);
      setError(null);
      pollRef.current = null;
      return;
    }

    let cancelled = false;

    function clearTimer() {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    }

    function scheduleNext(intervalMs: number) {
      if (cancelled || intervalMs === 0) return;
      timerRef.current = setTimeout(poll, intervalMs);
    }

    async function poll() {
      try {
        const res = await api.get<{ data: GenerationJob }>(`/api/bff/jobs/${jobId}`);
        if (cancelled) return;

        setJob(res.data);
        setError(null);

        const interval = INTERVAL_BY_STATUS[res.data.currentStep] ?? 5000;
        scheduleNext(interval);
      } catch (err) {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : 'Gagal memuat status job');
        scheduleNext(RETRY_INTERVAL_ON_ERROR);
      }
    }

    pollRef.current = poll;

    // Mulai polling segera
    poll();

    return () => {
      cancelled = true;
      pollRef.current = null;
      clearTimer();
    };
  }, [jobId]);

  /** Trigger poll segera, membatalkan timer yang sedang berjalan */
  const refresh = useCallback(() => {
    if (!pollRef.current) return;
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    pollRef.current();
  }, []);

  return { job, error, refresh };
}
